import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { deepEqual } from 'fast-equals';
import { connect } from 'react-redux';

import OrderHistoryFindOrders from 'components/account/OrderHistoryOverview/OrderHistoryFindOrders';
import OrderHistoryTableExpanded from 'components/account/OrderHistoryOverview/OrderHistoryTableExpanded';
import PaginationGroup from 'components/common/Pagination/PaginationGroup';
import LegacyPagination from 'components/account/Pagination';
import { VALID_ORDER } from 'common/regex';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { evPaginationResultsPerPageUpdate } from 'events/account';
import { track } from 'apis/amethyst';
import { resetCancelOrReturnItems, setPreviousOrderAction } from 'actions/account/account';
import { selectIsPaginationRedesignEnabled } from 'selectors/features';
import { ORDER_ACTION_NONE } from 'constants/orderActions';

import css from 'styles/components/account/orderHistoryOverview.scss';

const pageSizeValues = [10, 25, 50];

export class OrderHistoryOverview extends Component {
  static displayName = 'OrderHistoryOverview';
  constructor(props) {
    super(props);
    this.searchFormRef = React.createRef();
  }
  state = {
    searchByOrderIdError: false,
    returnablesAvailable: false
  };

  componentDidMount() {
    this.checkIfReturnablesAreAvailable();
  }

  componentDidUpdate(prevProps) {
    const { orders: prevOrders } = prevProps;
    const { orders } = this.props;
    // If orders are different, check returnables
    if (!deepEqual(prevOrders, orders)) {
      this.checkIfReturnablesAreAvailable(this.props);
    }
  }

  onReturnItems = () => {
    const { resetCancelOrReturnItems } = this.props;
    resetCancelOrReturnItems();
    this.context.router.forceBrowserPush('/returns');
  };

  checkIfReturnablesAreAvailable = (props = this.props) => {
    const { orders = [], isLoading } = props;
    this.setState({
      returnablesAvailable: !isLoading && orders.some(order => order.returnable)
    });
  };

  handleSearchOrderHistory = event => {
    event.preventDefault();
    const {
      location: { pathname }
    } = this.props;
    const {
      searchOrderHistory: { value }
    } = event.target;

    if (VALID_ORDER.test(value)) {
      this.setState({ searchByOrderIdError: false });
      this.context.router.pushPreserveAppRoot(`/orders/${value}`);
    } else {
      this.context.router.pushPreserveAppRoot(`${pathname}?search=${value}`);
      setTimeout(() => {
        // document.getElementById('order-search-form')?.scrollIntoView();
        this.searchFormRef.current?.scrollIntoView();
      }, 100);
    }
  };

  handleSearchByOrderIdSubmit = event => {
    event.preventDefault();
    const {
      searchOrderHistory: { value }
    } = event.target;
    const { setPreviousOrderAction } = this.props;
    setPreviousOrderAction(ORDER_ACTION_NONE);
    if (VALID_ORDER.test(value)) {
      this.setState({ searchByOrderIdError: false });
      this.context.router.pushPreserveAppRoot(`/orders/${value}`);
    } else {
      this.setState({ searchByOrderIdError: true });
    }
  };

  handleCancelSearchOrderHistory = () => {
    const {
      location: { pathname },
      cancelOrderHistorySearch
    } = this.props;
    cancelOrderHistorySearch();
    this.setState({ searchByOrderIdError: false });
    this.context.router.pushPreserveAppRoot(`${pathname}`);
  };

  handleUpdatePageSize = event => {
    const {
      location: { pathname, search },
      hasExpandedOrderHistorySearch,
      onFetchOrders,
      onFetchSearchOrders,
      pageType
    } = this.props;
    const { value } = event.target;
    const params = queryString.parse(search);
    const { pageSize = 10 } = params;
    const queryParams = { ...params, pageSize: value, page: undefined };

    track(() => [
      evPaginationResultsPerPageUpdate,
      {
        currentAmount: pageSize,
        newAmount: parseInt(value),
        sourcePage: pageType
      }
    ]);
    this.context.router.pushPreserveAppRoot(`${pathname}?${queryString.stringify(queryParams)}`);

    if (hasExpandedOrderHistorySearch && params.search) {
      onFetchSearchOrders(queryParams);
    } else {
      onFetchOrders(queryParams);
    }

    window.scrollTo(0, 0);
  };

  handleClickOrderLink = () => {
    const { setPreviousOrderAction } = this.props;
    setPreviousOrderAction(ORDER_ACTION_NONE);
  };

  makeSearchByOrderNumberErrorMsg() {
    return (
      <p className={css.errorMsg}>
        <strong>Oh No!</strong> Please enter a valid order number.
      </p>
    );
  }

  render() {
    const {
      location,
      ordersState: { uncollapsedOrderHistoryRowMap },
      orders,
      totalPages,
      isLoading,
      searchOrdersError,
      hasExpandedOrderHistorySearch,
      killswitch: { keepExchangeOrdersFromReturnCancel, showBatchReturn }
    } = this.props;
    const { searchByOrderIdError, returnablesAvailable } = this.state;

    return (
      <div className={css.orderHistoryOverview} data-test-id="orderHistoryOverview">
        {searchByOrderIdError && (
          <p className={css.errorMsg}>
            <strong>Oh No!</strong> Please enter a valid order number.
          </p>
        )}
        {searchOrdersError && (
          <p className={css.errorMsg}>
            <strong>Oh No!</strong> There was an issue with your search, please try again with a different term.
          </p>
        )}
        <div>
          <OrderHistoryFindOrders
            searchFormRef={this.searchFormRef}
            location={location}
            showBatchReturn={showBatchReturn}
            returnablesAvailable={returnablesAvailable}
            onCancelOrderHistorySearch={this.handleCancelSearchOrderHistory}
            onReturnItems={this.onReturnItems}
            onSearchOrderHistory={this.handleSearchOrderHistory}
            onSearchByOrderIdSubmit={this.handleSearchByOrderIdSubmit}
            hasExpandedOrderHistorySearch={hasExpandedOrderHistorySearch}
          />
        </div>
        <OrderHistoryTableExpanded
          orders={orders}
          onClickOrderLink={this.handleClickOrderLink}
          uncollapsedOrderHistoryRowMap={uncollapsedOrderHistoryRowMap}
          keepExchangeOrdersFromReturnCancel={keepExchangeOrdersFromReturnCancel}
          isLoading={isLoading}
        />
        {this.props.isPaginationRedesignEnabled ? (
          <PaginationGroup
            pageSizeValues={pageSizeValues}
            isOverview={true}
            location={location}
            totalPages={totalPages}
            onUpdatePageSize={this.handleUpdatePageSize}
          />
        ) : (
          <LegacyPagination
            pageSizeValues={pageSizeValues}
            isOverview={true}
            location={location}
            totalPages={totalPages}
            onUpdatePageSize={this.handleUpdatePageSize}
          />
        )}
      </div>
    );
  }
}

OrderHistoryOverview.contextTypes = {
  testId: PropTypes.func,
  router: PropTypes.object.isRequired
};

OrderHistoryOverview.propTypes = {
  location: PropTypes.object,
  orders: PropTypes.array.isRequired,
  totalPages: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onFetchOrders: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  pageType: state.pageView.pageType,
  killswitch: state.killswitch,
  ordersState: state.orders,
  isPaginationRedesignEnabled: selectIsPaginationRedesignEnabled(state)
});

export default withErrorBoundary(
  OrderHistoryOverview.displayName,
  connect(mapStateToProps, {
    resetCancelOrReturnItems,
    setPreviousOrderAction
  })(OrderHistoryOverview)
);
